import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styles from './UndoVote.module.scss';
import {
  Button,
  Icon,
  IconList,
  Text,
  Toast,
  createToast,
  ToastButton,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

type Props = {
  handleUndo?: () => Promise<void>;
  handleSkip?: () => Promise<void>;
  previouslyVotedStudyTitle?: string;
};

const UndoVote = ({
  handleUndo,
  handleSkip,
  previouslyVotedStudyTitle = '',
}: Props) => {
  const [title, setTitle] = useState('');
  const [showUndoVote, setShowUndoVote] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setTitle(previouslyVotedStudyTitle);
    setShowUndoVote(!!previouslyVotedStudyTitle);
  }, [previouslyVotedStudyTitle]);

  const onSkip = (): void => {
    setShowUndoVote(false);

    handleSkip?.();
  };

  const onUndo = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const triggerElement = event.currentTarget;
    setHasError(false);

    handleUndo?.().catch(() => {
      setHasError(true);

      createToast(
        <Toast type="danger" focusOnAppear restoreFocusTo={triggerElement}>
          Unable to undo vote. Please try again or skip
          <ToastButton onClick={handleSkip}>Skip</ToastButton>
        </Toast>
      );
    });
  };

  const renderUndoVote = () => (
    <>
      <div className={styles.ToolbarSeparator} />
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 1 }}
        transition={{ duration: 0.2, ease: 'easeIn' }}
        className={styles.animationWrapper}
      >
        <Inline
          alignY="top"
          alignX="spaceBetween"
          spacing={3}
          className={styles.UndoVoteWrapper}
          data-testid="undo-vote-container"
        >
          <Text component="span">{title}</Text>
          <Stack spacing={2} className={styles.UndoVotesActionWrapper}>
            <Button
              type="neutral"
              variant="outline"
              aria-label="Undo vote"
              data-pendo-key="undo-vote"
              onClick={onUndo}
            >
              <Icon icon={IconList.light.faArrowRotateLeft} />
              Undo
            </Button>
            {hasError && (
              <Button
                type="neutral"
                variant="ghost"
                aria-label="Skip undo vote"
                data-pendo-key="skip-undo-vote"
                onClick={onSkip}
              >
                Skip
              </Button>
            )}
          </Stack>
        </Inline>
      </motion.div>
    </>
  );

  return showUndoVote ? renderUndoVote() : null;
};

export default UndoVote;
